'use client';

import { Loader2 } from 'lucide-react';

import { useJoinChannel } from '../../hooks/useJoinChannel';
import type { CommunityChannel } from '../../types/CommunityChannel';
import { ChannelJoinErrorDialog } from './ChannelJoinErrorDialog';

interface Props {
  channel: CommunityChannel;
}

/**
 * Compact pill floated over the bottom of the message list when a non-owner
 * viewer hasn't joined the channel yet — 1:1 with PopshopRN's
 * `JoinChannelBanner` (its own `Center` host handles the centered floating
 * placement). Pressing "Join this Channel" POSTs the add-member call; the live
 * membership subscription (`useMyChannelMembership`) then flips the pane to the
 * real composer. A failed join surfaces an error alert.
 */
export function JoinChannelBanner({ channel }: Props) {
  const { join, joining, error, clearError } = useJoinChannel(channel);

  return (
    <div className="border-line-strong bg-surface-2 pointer-events-auto flex w-[min(345px,calc(100vw-2rem))] items-center justify-between gap-4 rounded-2xl border px-4 py-3 shadow-lg">
      <p className="text-content-1 text-sm font-medium">
        Want to get involved in the conversation?
      </p>
      <button
        type="button"
        onClick={() => void join()}
        disabled={joining}
        // The label is the width anchor: while joining it stays laid out but
        // `invisible` (so the button keeps its size and the question text never
        // reflows) and the spinner overlays it centered.
        className="bg-brand hover:bg-brand-hover-hover text-content-on-brand relative inline-flex shrink-0 items-center justify-center whitespace-nowrap rounded-lg px-4 py-2 text-sm font-semibold transition disabled:opacity-70"
      >
        <span className={joining ? 'invisible' : undefined}>
          Join this Channel
        </span>
        {joining ? (
          <Loader2
            className="absolute inset-0 m-auto h-4 w-4 animate-spin"
            aria-hidden
          />
        ) : null}
      </button>

      <ChannelJoinErrorDialog error={error} onClose={clearError} />
    </div>
  );
}
